import { api } from "../api";
import { getAnalyticsDateRange } from "./getAnalyticsDateRange";

/**
 * Fetch all analytics stats from Gadget views with streaming updates.
 * Each metric is displayed as soon as it's available.
 *
 * @param {Object} params
 * @param {string} params.customerId - The customer ID
 * @param {string} params.shopId - The shop ID (for turnover/sales)
 * @param {string} params.selectedDateFilter - The selected date filter (Today, ThisWeek, etc.)
 * @param {Date} params.startDate - Custom start date for "Between" filter
 * @param {Date} params.endDate - Custom end date for "Between" filter
 * @param {Function} params.onUpdate - Callback called with partial stats as they arrive
 * @returns {Promise<void>} Resolves when all metrics are fetched
 */
export const fetchAnalyticsStats = async ({
  customerId,
  shopId,
  selectedDateFilter,
  startDate,
  endDate,
  onUpdate,
}) => {
  const { from, to } = getAnalyticsDateRange(
    selectedDateFilter,
    startDate,
    endDate,
  );

  const commonParams = { customerId, from, to };
  const shopParams = { shopId, from, to };
  const suffix = selectedDateFilter;

  // Define all metrics to fetch
  const metricsFetchers = [
    {
      fetch: () => api.getAnalyticsSent(commonParams),
      transform: (result) => ({
        [`sent${suffix}`]: result?.messages?.sent ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsDelivered(commonParams),
      transform: (result) => ({
        [`delivered${suffix}`]: result?.messages?.delivered ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsRead(commonParams),
      transform: (result) => ({
        [`read${suffix}`]: result?.messages?.read ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsOptins(commonParams),
      transform: (result) => ({
        [`optins${suffix}`]: result?.contacts?.optins ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsOptouts(commonParams),
      transform: (result) => ({
        [`optoutCount${suffix}`]: result?.contacts?.optoutCount ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsMessagesCount(commonParams),
      transform: (result) => ({
        [`messagesCount${suffix}`]: result?.messages?.messagesCount ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsWhatsappCost(commonParams),
      transform: (result) => ({
        [`totalWhatsappCostInEuros${suffix}`]:
          result?.messages?.totalWhatsappCostInEuros ?? 0,
        totalWhatsappCostInEuros:
          result?.messages?.totalWhatsappCostInEuros ?? 0,
      }),
    },
    {
      fetch: () =>
        shopId ? api.getAnalyticsTurnover(shopParams) : Promise.resolve(null),
      transform: (result) => ({
        [`turnover${suffix}`]: result?.shopifyOrders?.turnover ?? 0,
      }),
    },
    {
      fetch: () =>
        shopId ? api.getAnalyticsSales(shopParams) : Promise.resolve(null),
      transform: (result) => ({
        [`sales${suffix}`]: result?.shopifyOrders?.sales ?? 0,
      }),
    },
    {
      fetch: () => api.getAnalyticsExternalTurnover(commonParams),
      transform: (result) => ({
        [`externalTurnover${suffix}`]: result?.messages?.externalTurnover ?? 0,
      }),
    },
  ];

  // Launch all fetches in parallel, update state as each completes
  const promises = metricsFetchers.map(async ({ fetch, transform }) => {
    try {
      const result = await fetch();
      const partialStats = transform(result);
      onUpdate((prev) => ({ ...prev, ...partialStats }));
    } catch (error) {
      console.error("Error fetching metric:", error);
    }
  });

  await Promise.all(promises);
};
